import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type EmploymentAnalysis } from '../../employmentService'
import {
  SectorEvolutionChart,
  type SectorEvolutionChartRef,
} from '../charts/SectorEvolutionChart'
import {
  TabContentContainer,
  MetricsGrid,
  ChartContainer,
} from '../../EmploymentTab.styles'

export interface EconomicSectorsTabProps {
  data: EmploymentAnalysis | null
  t: TFunction
}

export const EconomicSectorsTab: React.FC<EconomicSectorsTabProps> = ({
  data,
  t,
}) => {
  const sectorEvolutionChartRef = useRef<SectorEvolutionChartRef>(null)

  if (!data) return null

  const { current } = data

  // Calculate sector percentages
  const totalSectorEmployment =
    (current.employment_sector_1 || 0) +
    (current.employment_sector_2 || 0) +
    (current.employment_sector_3 || 0)
  const sector1Pct =
    totalSectorEmployment > 0
      ? ((current.employment_sector_1 || 0) / totalSectorEmployment) * 100
      : 0
  const sector2Pct =
    totalSectorEmployment > 0
      ? ((current.employment_sector_2 || 0) / totalSectorEmployment) * 100
      : 0
  const sector3Pct =
    totalSectorEmployment > 0
      ? ((current.employment_sector_3 || 0) / totalSectorEmployment) * 100
      : 0

  return (
    <TabContentContainer>
      {/* Sector Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('sectors.primary.name')}
          value={(current.employment_sector_1 || 0).toLocaleString()}
          subtitle={`${sector1Pct.toFixed(1)}% | ${(current.establishments_sector_1 || 0).toLocaleString()} ${t('common.establishments')}`}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.primarySector.title')}
              shortDescription={t('tooltips.primarySector.shortDescription')}
              calculation={t('tooltips.primarySector.calculation')}
              importance={t('tooltips.primarySector.importance')}
              limitations={t('tooltips.primarySector.limitations')}
              source={t('tooltips.primarySector.source')}
            />
          }
        />

        <MetricCard
          title={t('sectors.secondary.name')}
          value={(current.employment_sector_2 || 0).toLocaleString()}
          subtitle={`${sector2Pct.toFixed(1)}% | ${(current.establishments_sector_2 || 0).toLocaleString()} ${t('common.establishments')}`}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.secondarySector.title')}
              shortDescription={t('tooltips.secondarySector.shortDescription')}
              calculation={t('tooltips.secondarySector.calculation')}
              importance={t('tooltips.secondarySector.importance')}
              limitations={t('tooltips.secondarySector.limitations')}
              source={t('tooltips.secondarySector.source')}
            />
          }
        />

        <MetricCard
          title={t('sectors.tertiary.name')}
          value={(current.employment_sector_3 || 0).toLocaleString()}
          subtitle={`${sector3Pct.toFixed(1)}% | ${(current.establishments_sector_3 || 0).toLocaleString()} ${t('common.establishments')}`}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.tertiarySector.title')}
              shortDescription={t('tooltips.tertiarySector.shortDescription')}
              calculation={t('tooltips.tertiarySector.calculation')}
              importance={t('tooltips.tertiarySector.importance')}
              limitations={t('tooltips.tertiarySector.limitations')}
              source={t('tooltips.tertiarySector.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Charts */}
      <ChartContainer>
        <ChartHeader
          title={t('charts.sectorEvolution.title')}
          subtitle={t('charts.sectorEvolution.subtitle')}
          onDownload={() => {
            const dataUrl = sectorEvolutionChartRef.current?.exportChart()
            downloadChartAsPng(
              dataUrl,
              sanitizeFilename(t('charts.sectorEvolution.title'))
            )
          }}
          downloadLabel={t('common.downloadChart')}
        />
        <SectorEvolutionChart
          ref={sectorEvolutionChartRef}
          data={data.trends}
          height={400}
          t={t}
        />
      </ChartContainer>
    </TabContentContainer>
  )
}
